/* ==========================================================================
   AMOLIH JEWELLERY GIFT FINDER QUIZ - CONTROLLER & MATCHING ENGINE
   Handles: 5-step interactive navigation, dynamic Men vs Women adaptive flows,
   2-category limit enforcement, real Shopify catalogue retrieval,
   multi-attribute filter matching, currency-aware variant budget checks,
   URL query & 30-day cookie synchronization.
   ========================================================================== */

(function () {
  'use strict';

  // Allowed valid quiz options for strict sanitization
  const VALID_OPTIONS = {
    occasion: [
      'Birthday',
      'Anniversary',
      'Wedding & Engagement',
      'Graduation & Milestones',
      'Everyday Classics',
      'Cocktail & Evening',
      'Festive & Cultural',
      'Just Because',
      // Legacy backwards-compatibility
      'Wedding or Engagement',
      'Graduation or Milestone',
      'Everyday Classics / Just Because',
      'Cultural & Festive Celebration',
      'Not Sure / Explore All'
    ],
    recipient: ['Women', 'Men'],
    gender: ['women', 'men'],
    categories: [
      // Women styles
      'Rings',
      'Earrings',
      'Bracelets',
      'Necklaces',
      'Charms & Pendants',
      'Charms and Pendants',
      'Brooches',
      // Men styles
      'Rings & Signet Bands',
      'Bracelets & Cords',
      'Chains & Necklaces',
      'Pendants & Dog Tags',
      'Cufflinks',
      // Universal
      'Not Sure'
    ],
    personalization: [
      'Name Jewellery',
      'Lucky Numbers & Initials',
      'Love Letters',
      'Solitaire & Classic',
      'Bright & Colourful',
      'Bold & Opulent',
      'Timeless & Minimal',
      'Surprise Me',
      // Legacy backwards-compatibility
      'Initials or Names',
      'Birthstones or Gemstones',
      'Engraving & Special Numbers',
      'Classic Jewellery — No Personalisation',
      'Dog Tags, Initials & Numbers',
      'Engraved Coordinates & Cord Bracelets',
      'Signet Rings & Statement Bands',
      'Minimalist & Clean — No Personalisation',
      'Not Sure Yet'
    ],
    budget: [
      'tier-1',
      'tier-2',
      'tier-3',
      'tier-4',
      'tier-5',
      'tier-6',
      'under-5000',
      '5000-10000',
      '10000-30000',
      'above-30000',
      'all'
    ]
  };

  const COOKIE_NAME = 'amolih_gift_quiz_state';
  const COOKIE_MAX_AGE_DAYS = 30;
  const PRODUCTS_PER_PAGE = 12;

  class AmolihGiftQuiz {
    constructor() {
      this.dom = {
        container: document.getElementById('gift-quiz-container'),
        backBtn: document.getElementById('quiz-back-btn'),
        topRestartBtn: document.getElementById('quiz-top-restart-btn'),
        progressFill: document.getElementById('quiz-progress-fill'),
        progressBadge: document.getElementById('quiz-progress-badge'),
        progressStepNum: document.getElementById('quiz-progress-step-num'),
        stepIndicator: document.getElementById('quiz-step-indicator'),
        steps: document.querySelectorAll('.quiz-step'),
        budgetOptionsGrid: document.getElementById('quiz-options-step5'),
        step3ContinueBtn: document.getElementById('quiz-step3-continue-btn'),
        styleCounter: document.getElementById('quiz-style-counter'),
        limitNotice: document.getElementById('quiz-limit-notice'),
        resultsContainer: document.getElementById('quiz-results-container'),
        resultsCountText: document.getElementById('quiz-results-count-text'),
        summaryBadges: document.getElementById('quiz-summary-badges'),
        productGrid: document.getElementById('quiz-product-grid'),
        loadMoreWrap: document.getElementById('quiz-load-more-wrap'),
        loadMoreBtn: document.getElementById('quiz-load-more-btn'),
        emptyState: document.getElementById('quiz-empty-state'),
        editAnswersBtn: document.getElementById('quiz-edit-answers-btn'),
        restartBtn: document.getElementById('quiz-restart-btn'),
        emptyBroadenBtn: document.getElementById('quiz-empty-broaden-btn'),
        emptyEditBtn: document.getElementById('quiz-empty-edit-btn')
      };

      if (!this.dom.container) return;

      // Configuration from Liquid
      this.config = this.readConfig();

      // State
      this.state = {
        occasion: '',
        recipient: '',
        gender: '', // initially unselected
        categories: [],
        personalization: '',
        budget: '',
        step: 1
      };

      this.catalogue = [];
      this.matchingProducts = [];
      this.displayedCount = 0;
      this.isCatalogueLoading = false;

      this.init();
    }

    readConfig() {
      const configEl = document.getElementById('AmolihQuizConfig');
      let parsed = null;
      if (configEl) {
        try {
          parsed = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Error parsing quiz config', e);
        }
      }

      const cfg = {
        countryCode: (parsed?.countryCode || 'US').toUpperCase(),
        countryName: parsed?.countryName || 'United States',
        currency: (parsed?.currency || 'USD').toUpperCase(),
        currencySymbol: parsed?.currencySymbol || '$',
        rootUrl: parsed?.rootUrl || '/',
        moneyFormat: parsed?.moneyFormat || '${{amount}}'
      };

      // Determine active market strictly from Shopify server-rendered config or URL pathname prefix
      const path = window.location.pathname || '';
      if (path.startsWith('/en-gb')) {
        cfg.countryCode = 'GB';
        cfg.countryName = 'United Kingdom';
        cfg.currency = 'GBP';
        cfg.currencySymbol = '£';
        cfg.rootUrl = '/en-gb/';
      } else if (path.startsWith('/en-in')) {
        cfg.countryCode = 'IN';
        cfg.countryName = 'India';
        cfg.currency = 'INR';
        cfg.currencySymbol = '₹';
        cfg.rootUrl = '/en-in/';
      } else if (parsed?.countryCode && parsed.countryCode !== 'US' && parsed.rootUrl && parsed.rootUrl !== '/') {
        cfg.countryCode = parsed.countryCode.toUpperCase();
        cfg.countryName = parsed.countryName || cfg.countryCode;
        cfg.currency = (parsed.currency || 'USD').toUpperCase();
        cfg.currencySymbol = parsed.currencySymbol || '$';
        cfg.rootUrl = parsed.rootUrl;
      } else {
        // Default Primary Market (US / Root)
        cfg.countryCode = 'US';
        cfg.countryName = 'United States';
        cfg.currency = (parsed?.currency && parsed.currency !== 'INR' && parsed.currency !== 'GBP') ? parsed.currency : 'USD';
        cfg.currencySymbol = '$';
        cfg.rootUrl = '/';
      }

      return cfg;
    }

    getBudgetTiers(currencyCode) {
      const cur = (currencyCode || this.config.currency || 'USD').toUpperCase();

      if (cur === 'INR') {
        return {
          'tier-1': { label: 'Below ₹5,000.00', min: 0, max: 5000 },
          'tier-2': { label: '₹5,001.00 - ₹10,000.00', min: 5000, max: 10000 },
          'tier-3': { label: '₹10,001.00 - ₹15,000.00', min: 10000, max: 15000 },
          'tier-4': { label: '₹15,001.00 - ₹30,000.00', min: 15000, max: 30000 },
          'tier-5': { label: '₹30,001.00 - ₹75,000.00', min: 30000, max: 75000 },
          'tier-6': { label: 'Above ₹75,000.00', min: 75000, max: Infinity }
        };
      } else if (cur === 'GBP') {
        return {
          'tier-1': { label: 'Below £100.00', min: 0, max: 100 },
          'tier-2': { label: '£100.00 - £250.00', min: 100, max: 250 },
          'tier-3': { label: '£250.00 - £500.00', min: 250, max: 500 },
          'tier-4': { label: '£500.00 - £1,000.00', min: 500, max: 1000 },
          'tier-5': { label: '£1,000.00 - £2,000.00', min: 1000, max: 2000 },
          'tier-6': { label: 'Above £2,000.00', min: 2000, max: Infinity }
        };
      } else if (cur === 'CAD') {
        return {
          'tier-1': { label: 'Below $100.00 CAD', min: 0, max: 100 },
          'tier-2': { label: '$100.00 - $250.00 CAD', min: 100, max: 250 },
          'tier-3': { label: '$250.00 - $500.00 CAD', min: 250, max: 500 },
          'tier-4': { label: '$500.00 - $1,000.00 CAD', min: 500, max: 1000 },
          'tier-5': { label: '$1,000.00 - $2,000.00 CAD', min: 1000, max: 2000 },
          'tier-6': { label: 'Above $2,000.00 CAD', min: 2000, max: Infinity }
        };
      } else if (cur === 'AUD') {
        return {
          'tier-1': { label: 'Below $100.00 AUD', min: 0, max: 100 },
          'tier-2': { label: '$100.00 - $250.00 AUD', min: 100, max: 250 },
          'tier-3': { label: '$250.00 - $500.00 AUD', min: 250, max: 500 },
          'tier-4': { label: '$500.00 - $1,000.00 AUD', min: 500, max: 1000 },
          'tier-5': { label: '$1,000.00 - $2,000.00 AUD', min: 1000, max: 2000 },
          'tier-6': { label: 'Above $2,000.00 AUD', min: 2000, max: Infinity }
        };
      } else if (cur === 'EUR') {
        return {
          'tier-1': { label: 'Below €100.00', min: 0, max: 100 },
          'tier-2': { label: '€100.00 - €250.00', min: 100, max: 250 },
          'tier-3': { label: '€250.00 - €500.00', min: 250, max: 500 },
          'tier-4': { label: '€500.00 - €1,000.00', min: 500, max: 1000 },
          'tier-5': { label: '€1,000.00 - €2,000.00', min: 1000, max: 2000 },
          'tier-6': { label: 'Above €2,000.00', min: 2000, max: Infinity }
        };
      }

      // Default: USD
      return {
        'tier-1': { label: 'Below $100.00', min: 0, max: 100 },
        'tier-2': { label: '$100.00 - $250.00', min: 100, max: 250 },
        'tier-3': { label: '$250.00 - $500.00', min: 250, max: 500 },
        'tier-4': { label: '$500.00 - $1,000.00', min: 500, max: 1000 },
        'tier-5': { label: '$1,000.00 - $2,000.00', min: 1000, max: 2000 },
        'tier-6': { label: 'Above $2,000.00', min: 2000, max: Infinity }
      };
    }

    renderBudgetOptions() {
      const grid = this.dom.budgetOptionsGrid;
      if (!grid) return;
      const tiers = this.getBudgetTiers(this.config.currency);
      const currentVal = this.state.budget;

      let html = '';
      for (const [key, tier] of Object.entries(tiers)) {
        html += `<button type="button" class="quiz-option-btn${currentVal === key ? ' is-selected' : ''}" data-option-val="${key}">${tier.label}</button>\n`;
      }
      html += `<button type="button" class="quiz-option-btn quiz-option-btn--wide${currentVal === 'all' ? ' is-selected' : ''}" data-option-val="all">Show All Prices</button>`;

      grid.innerHTML = html;

      grid.querySelectorAll('[data-option-val]').forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.getAttribute('data-option-val');
          this.handleSingleSelect('budget', val, 6);
        });
      });
    }

    init() {
      // 1. Initial budget options in active market currency
      this.renderBudgetOptions();

      // 2. Fetch real catalogue early in background
      this.fetchCatalogue();

      // 3. Restore state from URL or Cookie
      this.restoreInitialState();

      // 4. Bind UI event listeners
      this.bindEvents();

      // 5. Render initial step
      this.renderCurrentState();
    }

    /* ------------------------------------------------------------------------
       Catalogue Loading (Market-Aware Multi-Region Catalogue)
       ------------------------------------------------------------------------ */
    async fetchCatalogue(force = false) {
      if (this.catalogue.length > 0 && !force) return;
      if (this.isCatalogueLoading) return;
      this.isCatalogueLoading = true;

      const code = (this.config.countryCode || 'US').toUpperCase();
      const cleanRoot = (this.config.rootUrl || '/').replace(/\/$/, '');

      // Candidate URLs based on active market
      let primaryUrl = `${cleanRoot}/products.json?limit=250`;
      let fallbackUrls = [];

      if (code === 'GB') {
        primaryUrl = `${cleanRoot}/products.json?limit=250`;
        fallbackUrls = [
          'https://amolih.com/en-gb/products.json?limit=250',
          '/products.json?limit=250'
        ];
      } else if (code === 'IN') {
        primaryUrl = `${cleanRoot}/products.json?limit=250`;
        fallbackUrls = [
          'https://amolih.com/en-in/products.json?limit=250',
          '/products.json?limit=250'
        ];
      } else {
        primaryUrl = `${cleanRoot}/products.json?limit=250`;
        fallbackUrls = [
          'https://amolih.com/products.json?limit=250',
          '/products.json?limit=250'
        ];
      }

      const tryFetch = async (fetchUrl) => {
        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        return data.products || [];
      };

      try {
        let products = [];
        try {
          products = await tryFetch(primaryUrl);
        } catch (e1) {
          for (const fb of fallbackUrls) {
            try {
              products = await tryFetch(fb);
              if (products.length > 0) break;
            } catch (e2) { }
          }
        }

        // Validate market currency sanity in local dev proxy environment:
        // If config is USD or GBP, but sample prices reflect INR (> 1000 for stud earrings),
        // fetch authentic market prices from live market endpoints
        if (products.length > 0) {
          const samplePrice = parseFloat(products[0]?.variants?.[0]?.price || '0');
          if ((code === 'US' || code === 'GB') && samplePrice > 1000) {
            try {
              const liveMarketUrl = code === 'GB'
                ? 'https://amolih.com/en-gb/products.json?limit=250'
                : 'https://amolih.com/products.json?limit=250';
              const liveProducts = await tryFetch(liveMarketUrl);
              if (liveProducts.length > 0) products = liveProducts;
            } catch (eLive) { }
          }
        }

        this.catalogue = products;
        this.isCatalogueLoading = false;

        // If user already on results step, run match now
        if (this.state.step === 6) {
          this.computeAndRenderResults();
        }
      } catch (err) {
        console.warn(`Error fetching AMOLIH products for ${this.config.countryCode}:`, err);
        this.isCatalogueLoading = false;
        if (this.dom.resultsCountText && this.state.step === 6) {
          this.dom.resultsCountText.textContent = 'Products are loading for your region...';
        }
      }
    }

    /* ------------------------------------------------------------------------
       State Management, URL & Cookie Persistence
       ------------------------------------------------------------------------ */
    restoreInitialState() {
      const urlParams = new URLSearchParams(window.location.search);
      const urlOccasion = urlParams.get('occasion');
      const urlRecipient = urlParams.get('recipient');
      const urlGender = urlParams.get('gender');
      const urlCategories = urlParams.get('categories');
      const urlPersonalization = urlParams.get('personalization');
      const urlBudget = urlParams.get('budget');
      const urlStep = urlParams.get('step');

      let hasValidUrlParams = false;
      const restored = {
        occasion: '',
        recipient: '',
        gender: '',
        categories: [],
        personalization: '',
        budget: '',
        step: 1
      };

      if (urlOccasion && VALID_OPTIONS.occasion.includes(urlOccasion)) {
        restored.occasion = urlOccasion;
        hasValidUrlParams = true;
      }
      if (urlRecipient && VALID_OPTIONS.recipient.includes(urlRecipient)) {
        restored.recipient = urlRecipient;
        hasValidUrlParams = true;
        if (urlGender && VALID_OPTIONS.gender.includes(urlGender)) {
          restored.gender = urlGender;
        } else {
          restored.gender = this.inferGenderFromRecipient(restored.recipient);
        }
      }

      if (urlCategories) {
        const cats = urlCategories.split(',').map(c => {
          const trimmed = c.trim();
          if (trimmed === 'Chains & Necklaces') return 'Necklaces';
          if (trimmed === 'Bracelets & Cords') return 'Bracelets';
          if (trimmed === 'Pendants & Dog Tags') return 'Charms & Pendants';
          if (trimmed === 'Charms and Pendants') return 'Charms & Pendants';
          return trimmed;
        }).filter(c => VALID_OPTIONS.categories.includes(c));
        if (cats.length > 0) {
          restored.categories = [...new Set(cats)].slice(0, 2);
          hasValidUrlParams = true;
        }
      }
      if (urlPersonalization && VALID_OPTIONS.personalization.includes(urlPersonalization)) {
        restored.personalization = urlPersonalization;
        hasValidUrlParams = true;
      }
      if (urlBudget && VALID_OPTIONS.budget.includes(urlBudget)) {
        restored.budget = urlBudget;
        hasValidUrlParams = true;
      }

      if (hasValidUrlParams) {
        if (urlStep === 'results' || (restored.occasion && restored.recipient && restored.categories.length && restored.personalization && restored.budget)) {
          restored.step = 6;
        } else if (urlStep && !isNaN(parseInt(urlStep, 10))) {
          const stepNum = parseInt(urlStep, 10);
          restored.step = Math.min(Math.max(stepNum, 1), 6);
        } else {
          restored.step = this.calculateNextIncompleteStep(restored);
        }
        this.state = restored;
        this.syncStateToCookie();
        this.syncStateToUrl();
        return;
      }

      // Fallback: Check 30-day Cookie (e.g. after country switch redirect)
      const cookieState = this.readCookieState();
      if (cookieState) {
        this.state = cookieState;
        this.syncStateToUrl();
      }
    }

    inferGenderFromRecipient(recipient) {
      if (!recipient) return 'women';
      const r = recipient.trim().toLowerCase();
      if (r === 'women' || r === 'for her' || r === 'mother' || r === 'sister' || r === 'wife' || r === 'girlfriend' || r === 'daughter') {
        return 'women';
      }
      if (r === 'men' || r === 'for him' || r === 'father' || r === 'brother' || r === 'husband' || r === 'boyfriend' || r === 'son') {
        return 'men';
      }
      if (/\b(him|men|dad|father|brother|son|husband|groom)\b/i.test(r)) {
        return 'men';
      }
      return 'women';
    }

    calculateNextIncompleteStep(st) {
      if (!st.occasion) return 1;
      if (!st.recipient) return 2;
      if (!st.categories || !st.categories.length) return 3;
      if (!st.personalization) return 4;
      if (!st.budget) return 5;
      return 6;
    }

    readCookieState() {
      const name = COOKIE_NAME + '=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i].trim();
        if (c.indexOf(name) === 0) {
          try {
            const parsed = JSON.parse(c.substring(name.length));
            return this.validateAndSanitizeState(parsed);
          } catch (e) {
            return null;
          }
        }
      }
      return null;
    }

    validateAndSanitizeState(raw) {
      if (!raw || typeof raw !== 'object') return null;
      const clean = {
        occasion: VALID_OPTIONS.occasion.includes(raw.occasion) ? raw.occasion : '',
        recipient: VALID_OPTIONS.recipient.includes(raw.recipient) ? raw.recipient : '',
        gender: raw.gender === 'men' ? 'men' : 'women',
        categories: Array.isArray(raw.categories)
          ? [...new Set(raw.categories.map(c => {
            if (c === 'Chains & Necklaces') return 'Necklaces';
            if (c === 'Bracelets & Cords') return 'Bracelets';
            if (c === 'Pendants & Dog Tags') return 'Charms & Pendants';
            if (c === 'Charms and Pendants') return 'Charms & Pendants';
            return c;
          }).filter(c => VALID_OPTIONS.categories.includes(c)))].slice(0, 2)
          : [],
        personalization: VALID_OPTIONS.personalization.includes(raw.personalization) ? raw.personalization : '',
        budget: VALID_OPTIONS.budget.includes(raw.budget) ? raw.budget : '',
        step: 1
      };

      if (!clean.gender && clean.recipient) {
        clean.gender = this.inferGenderFromRecipient(clean.recipient);
      }

      if (raw.step && !isNaN(parseInt(raw.step, 10))) {
        clean.step = Math.min(Math.max(parseInt(raw.step, 10), 1), 6);
      } else if (clean.occasion && clean.recipient && clean.categories.length && clean.personalization && clean.budget) {
        clean.step = 6;
      } else {
        clean.step = this.calculateNextIncompleteStep(clean);
      }
      return clean;
    }

    syncStateToUrl() {
      const url = new URL(window.location);
      // Clean country query parameters: Shopify native markets manage locale via path prefix
      url.searchParams.delete('country');
      url.searchParams.delete('country_code');

      if (this.state.occasion) url.searchParams.set('occasion', this.state.occasion);
      else url.searchParams.delete('occasion');

      if (this.state.recipient) {
        url.searchParams.set('recipient', this.state.recipient);
        if (this.state.gender) url.searchParams.set('gender', this.state.gender);
        else url.searchParams.delete('gender');
      } else {
        url.searchParams.delete('recipient');
        url.searchParams.delete('gender');
      }

      if (this.state.categories && this.state.categories.length > 0) {
        url.searchParams.set('categories', this.state.categories.join(','));
      } else {
        url.searchParams.delete('categories');
      }

      if (this.state.personalization) url.searchParams.set('personalization', this.state.personalization);
      else url.searchParams.delete('personalization');

      if (this.state.budget) url.searchParams.set('budget', this.state.budget);
      else url.searchParams.delete('budget');

      if (this.state.step === 6) {
        url.searchParams.set('step', 'results');
      } else if (this.state.step > 1) {
        url.searchParams.set('step', this.state.step.toString());
      } else {
        url.searchParams.delete('step');
      }

      window.history.replaceState({ quizStep: this.state.step }, '', url.toString());
    }

    syncStateToCookie() {
      const data = {
        occasion: this.state.occasion,
        recipient: this.state.recipient,
        gender: this.state.gender,
        categories: this.state.categories,
        personalization: this.state.personalization,
        budget: this.state.budget,
        step: this.state.step,
        timestamp: Date.now()
      };
      const json = encodeURIComponent(JSON.stringify(data));
      const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
      document.cookie = `${COOKIE_NAME}=${json}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }

    clearSavedState() {
      this.state = {
        occasion: '',
        recipient: '',
        gender: '',
        categories: [],
        personalization: '',
        budget: '',
        step: 1
      };
      document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
      const url = new URL(window.location);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }

    /* ------------------------------------------------------------------------
       Event Binding
       ------------------------------------------------------------------------ */
    bindEvents() {
      // Capture country selector interactions & forms to persist state immediately before Shopify page redirect
      document.addEventListener('click', (e) => {
        if (e.target.closest('.country-selector__list, .localization-form__list, .country-filter, [on\\:click*="selectCountry"]')) {
          this.syncStateToCookie();
        }
      }, true);

      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form');
        if (form && (form.getAttribute('action')?.includes('/localization') || form.querySelector('input[name="country_code"]'))) {
          this.syncStateToCookie();
        }
      }, true);

      // Top controls
      if (this.dom.backBtn) {
        this.dom.backBtn.addEventListener('click', () => this.handleBack());
      }
      if (this.dom.topRestartBtn) {
        this.dom.topRestartBtn.addEventListener('click', () => this.handleRestart());
      }

      // Results controls
      if (this.dom.restartBtn) {
        this.dom.restartBtn.addEventListener('click', () => this.handleRestart());
      }
      if (this.dom.editAnswersBtn) {
        this.dom.editAnswersBtn.addEventListener('click', () => this.goToStep(1));
      }

      // Empty state buttons
      if (this.dom.emptyBroadenBtn) {
        this.dom.emptyBroadenBtn.addEventListener('click', () => {
          this.state.budget = 'all';
          this.syncStateToUrl();
          this.syncStateToCookie();
          this.computeAndRenderResults();
        });
      }
      if (this.dom.emptyEditBtn) {
        this.dom.emptyEditBtn.addEventListener('click', () => this.goToStep(3));
      }

      // Step 1 Options (Occasion)
      const step1Btns = document.querySelectorAll('#quiz-step-1 [data-option-val]');
      step1Btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.getAttribute('data-option-val');
          this.handleSingleSelect('occasion', val, 2);
        });
      });

      // Step 2 Options (Recipient) - GENDER SELECTION (WOMEN & MEN)
      const genderCards = document.querySelectorAll('.quiz-gender-card');
      genderCards.forEach(card => {
        const selectGender = () => {
          const val = card.getAttribute('data-option-val') || 'Women';
          const gender = card.getAttribute('data-recipient-gender') || (val.toLowerCase() === 'men' ? 'men' : 'women');

          genderCards.forEach(c => {
            c.classList.remove('is-selected');
            c.setAttribute('aria-checked', 'false');
          });
          card.classList.add('is-selected');
          card.setAttribute('aria-checked', 'true');

          // If gender changed, reset style and personalisation to prevent cross-gender pollution
          if (this.state.gender !== gender) {
            this.state.categories = [];
            this.state.personalization = '';
          }

          this.state.gender = gender;
          this.state.recipient = val;
          if (this.dom.container) {
            this.dom.container.setAttribute('data-active-gender', gender);
          }
          this.syncStateToUrl();
          this.syncStateToCookie();

          setTimeout(() => {
            this.goToStep(3);
          }, 220);
        };

        card.addEventListener('click', selectGender);
        card.addEventListener('keydown', e => {
          if (card.tagName.toLowerCase() !== 'button' && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            selectGender();
          }
        });
      });

      // Step 3 Options (Jewellery Style - Visual Cards for Women and Men)
      const styleCards = document.querySelectorAll('.quiz-style-card');
      styleCards.forEach(card => {
        card.addEventListener('click', () => {
          const val = card.getAttribute('data-style-val');
          this.handleStyleCardToggle(val, card);
        });
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const val = card.getAttribute('data-style-val');
            this.handleStyleCardToggle(val, card);
          }
        });
      });

      // Step 3 Continue button
      if (this.dom.step3ContinueBtn) {
        this.dom.step3ContinueBtn.addEventListener('click', () => {
          if (this.state.categories.length > 0) {
            this.goToStep(4);
          }
        });
      }

      // Step 4 Options (Personalisation for Women & Men)
      const step4Btns = document.querySelectorAll('#quiz-step-4 [data-option-val]');
      step4Btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.getAttribute('data-option-val');
          this.handleSingleSelect('personalization', val, 5);
        });
      });

      // Step 5 Options (Budget)
      const step5Btns = document.querySelectorAll('#quiz-step-5 [data-option-val]');
      step5Btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.getAttribute('data-option-val');
          this.handleSingleSelect('budget', val, 6);
        });
      });

      // Load more products
      if (this.dom.loadMoreBtn) {
        this.dom.loadMoreBtn.addEventListener('click', () => this.renderNextProductBatch());
      }

      // Click delegation on product cards (matches All Jewellery ProductCard navigation)
      if (this.dom.productGrid) {
        this.dom.productGrid.addEventListener('click', (e) => {
          const wishlistBtn = e.target.closest('[data-wishlist-button]');
          if (wishlistBtn) {
            e.stopPropagation();
            return;
          }

          const arrowBtn = e.target.closest('[data-quiz-arrow]');
          if (arrowBtn) {
            e.preventDefault();
            e.stopPropagation();
            const dir = arrowBtn.getAttribute('data-quiz-arrow');
            this.handleArrowClick(e, dir);
            return;
          }

          const card = e.target.closest('.quiz-product-card');
          if (card) {
            const url = card.getAttribute('data-product-url') || card.querySelector('a[href]')?.href;
            if (url) {
              const shouldOpenInNewTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
              if (shouldOpenInNewTab) {
                e.preventDefault();
                window.open(url, '_blank');
              } else {
                e.preventDefault();
                window.location.href = url;
              }
            }
          }
        });
      }

      // Keyboard navigation
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.state.step > 1 && this.state.step < 6) {
          this.handleBack();
        }
      });

      // Browser popstate (Back/Forward buttons)
      window.addEventListener('popstate', e => {
        if (e.state && e.state.quizStep) {
          this.state.step = e.state.quizStep;
          this.renderCurrentState();
        } else {
          this.restoreInitialState();
          this.renderCurrentState();
        }
      });
    }

    handleArrowClick(e, direction) {
      const arrowBtn = e.target.closest('[data-quiz-arrow]');
      if (!arrowBtn) return;
      const card = arrowBtn.closest('.quiz-product-card');
      if (!card) return;

      const slides = card.querySelectorAll('.quiz-product-card__slide');
      if (!slides || slides.length <= 1) return;

      let currentIndex = parseInt(card.getAttribute('data-current-slide') || '0', 10);
      if (isNaN(currentIndex)) currentIndex = 0;

      if (direction === 'next') {
        currentIndex = (currentIndex + 1) % slides.length;
      } else {
        currentIndex = (currentIndex - 1 + slides.length) % slides.length;
      }

      card.setAttribute('data-current-slide', currentIndex.toString());

      // Update active slide class
      slides.forEach((sl, idx) => {
        sl.classList.toggle('is-active', idx === currentIndex);
      });

      const activeSlide = slides[currentIndex];
      if (!activeSlide) return;

      const targetUrl = activeSlide.getAttribute('data-product-url');
      const formattedPrice = activeSlide.getAttribute('data-formatted-price');
      const formattedComparePrice = activeSlide.getAttribute('data-formatted-compare-price');
      const isOnSale = activeSlide.getAttribute('data-is-on-sale') === 'true';
      const variantId = activeSlide.getAttribute('data-variant-id');
      const variantTitle = activeSlide.getAttribute('data-variant-title');
      const priceVal = activeSlide.getAttribute('data-price');
      const comparePriceVal = activeSlide.getAttribute('data-compare-price');
      const activeImg = activeSlide.querySelector('img');
      const imgSrc = activeImg ? activeImg.src : '';

      // 1. Update Card & Anchor URLs
      if (targetUrl) {
        card.setAttribute('data-product-url', targetUrl);
        const mediaLink = card.querySelector('.quiz-product-card__media-link');
        if (mediaLink) mediaLink.href = targetUrl;
        const titleLink = card.querySelector('.quiz-product-card__title-link');
        if (titleLink) titleLink.href = targetUrl;
      }

      // 2. Update Price & Compare Price
      const priceEl = card.querySelector('.quiz-product-card__price');
      if (priceEl && formattedPrice) {
        priceEl.textContent = formattedPrice;
      }

      const comparePriceEl = card.querySelector('.quiz-product-card__compare-price');
      if (comparePriceEl) {
        if (isOnSale && formattedComparePrice) {
          comparePriceEl.textContent = formattedComparePrice;
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // 3. Update Sale Badge
      const saleBadge = card.querySelector('.quiz-product-card__badge-sale');
      if (saleBadge) {
        saleBadge.style.display = isOnSale ? '' : 'none';
      }

      // 4. Update Wishlist Button for this variant
      const wishlistBtn = card.querySelector('[data-wishlist-button]');
      if (wishlistBtn && variantId) {
        wishlistBtn.dataset.variantId = variantId;
        if (variantTitle) wishlistBtn.dataset.variantTitle = variantTitle;
        if (imgSrc) wishlistBtn.dataset.variantImage = imgSrc;
        if (priceVal) wishlistBtn.dataset.variantPrice = priceVal;
        wishlistBtn.dataset.variantCompareAtPrice = comparePriceVal || '0';
        if (window.AmolihWishlist?.syncButton) {
          window.AmolihWishlist.syncButton(wishlistBtn);
        }
      }
    }

    handleCountryChanged(detail) {
      if (!detail || !detail.countryCode) return;
      const code = detail.countryCode.toUpperCase();

      this.config.countryCode = code;
      this.config.countryName = detail.countryName || code;
      this.config.currency = (detail.currency || 'USD').toUpperCase();
      this.config.currencySymbol = detail.currencySymbol || '$';

      if (code === 'GB') this.config.rootUrl = '/en-gb/';
      else if (code === 'IN') this.config.rootUrl = '/en-in/';
      else this.config.rootUrl = '/';

      // 1. Re-render Step 5 budget options with new currency symbols and tiers
      this.renderBudgetOptions();

      // 2. Fetch catalogue for the newly selected country
      this.fetchCatalogue(true);

      // 3. Update summary badges if on results step
      if (this.state.step === 6) {
        this.renderSummaryBadges();
      }

      // 4. Update URL parameter & cookie
      this.syncStateToUrl();
      this.syncStateToCookie();
    }

    handleSingleSelect(field, val, nextStep) {
      this.state[field] = val;
      this.syncStateToUrl();
      this.syncStateToCookie();

      // Highlight button in active step immediately
      const activeStepEl = document.querySelector(`.quiz-step[data-step="${this.state.step}"]`);
      if (activeStepEl) {
        activeStepEl.querySelectorAll('[data-option-val]').forEach(b => {
          b.classList.toggle('is-selected', b.getAttribute('data-option-val') === val);
        });
      }

      // Advance fluidly to next step
      setTimeout(() => {
        this.goToStep(nextStep);
      }, 220);
    }

    handleStyleCardToggle(val, cardEl) {
      if (val === 'Not Sure') {
        if (this.state.categories.includes('Not Sure')) {
          this.state.categories = [];
        } else {
          this.state.categories = ['Not Sure'];
        }
      } else {
        let cats = this.state.categories.filter(c => c !== 'Not Sure');

        if (cats.includes(val)) {
          cats = cats.filter(c => c !== val);
          this.hideLimitNotice();
        } else {
          if (cats.length >= 2) {
            this.showLimitNotice('Maximum 2 styles selected. Deselect one to choose another.');
            return;
          }
          cats.push(val);
          this.hideLimitNotice();
        }
        this.state.categories = cats;
      }

      this.syncStateToUrl();
      this.syncStateToCookie();
      this.renderStyleCardsUI();
    }

    showLimitNotice(msg) {
      if (this.dom.limitNotice) {
        this.dom.limitNotice.textContent = msg;
        this.dom.limitNotice.classList.add('is-visible');
        clearTimeout(this._limitTimer);
        this._limitTimer = setTimeout(() => this.hideLimitNotice(), 3200);
      }
    }

    hideLimitNotice() {
      if (this.dom.limitNotice) {
        this.dom.limitNotice.classList.remove('is-visible');
      }
    }

    renderStyleCardsUI() {
      const isMen = this.state.gender === 'men';
      let activeGrid = document.getElementById('quiz-styles-women');
      if (isMen) activeGrid = document.getElementById('quiz-styles-men');

      const cards = (activeGrid || document).querySelectorAll('.quiz-style-card');

      cards.forEach(card => {
        const val = card.getAttribute('data-style-val');
        const isSelected = this.state.categories.includes(val);
        card.classList.toggle('is-selected', isSelected);
        card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      const count = this.state.categories.length;
      if (this.dom.styleCounter) {
        if (this.state.categories.includes('Not Sure')) {
          this.dom.styleCounter.textContent = 'All styles welcome';
        } else {
          this.dom.styleCounter.textContent = `${count} of 2 selected`;
        }
      }

      if (this.dom.step3ContinueBtn) {
        this.dom.step3ContinueBtn.disabled = count === 0;
      }
    }

    handleBack() {
      if (this.state.step > 1) {
        this.goToStep(this.state.step - 1);
      }
    }

    handleRestart() {
      this.clearSavedState();
      this.goToStep(1);
    }

    goToStep(stepNum) {
      this.state.step = stepNum;
      this.syncStateToUrl();
      this.syncStateToCookie();
      this.renderCurrentState();

      if (this.dom.container) {
        const top = this.dom.container.getBoundingClientRect().top + window.pageYOffset - 80;
        if (window.pageYOffset > top) {
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    }

    updateStep3UIForGender() {
      const isMen = this.state.gender === 'men';

      if (this.dom.container) {
        this.dom.container.setAttribute('data-active-gender', this.state.gender);
      }

      const step3Title = document.getElementById('quiz-step-3-title');
      if (step3Title) {
        step3Title.textContent = 'What jewellery do they love?';
      }

      const womenGrid = document.getElementById('quiz-styles-women');
      const menGrid = document.getElementById('quiz-styles-men');

      if (womenGrid) womenGrid.style.display = !isMen ? 'grid' : 'none';
      if (menGrid) menGrid.style.display = isMen ? 'grid' : 'none';
    }

    updateStep4UIForGender() {
      const isMen = this.state.gender === 'men';

      if (this.dom.container) {
        this.dom.container.setAttribute('data-active-gender', this.state.gender);
      }

      const step4Title = document.getElementById('quiz-step-4-title');
      if (step4Title) {
        step4Title.textContent = 'What makes their style special?';
      }

      const step4Subtitle = document.getElementById('quiz-step-4-subtitle');
      if (step4Subtitle) {
        step4Subtitle.textContent = 'Choose what reflects their personality and aesthetic.';
      }

      const womenStep4 = document.getElementById('quiz-options-step4-women');
      const menStep4 = document.getElementById('quiz-options-step4-men');

      if (womenStep4) womenStep4.style.display = !isMen ? 'grid' : 'none';
      if (menStep4) menStep4.style.display = isMen ? 'grid' : 'none';
    }

    renderCurrentState() {
      const step = this.state.step;
      try { document.title = 'The AMOLIH Gift Finder | AMOLIH'; } catch (e) {}

      if (this.dom.container) {
        this.dom.container.classList.toggle('gift-quiz-container--results', step === 6);
      }

      if (this.dom.backBtn) {
        const showBack = step > 1;
        this.dom.backBtn.hidden = !showBack;
        this.dom.backBtn.classList.toggle('is-hidden', !showBack);
      }

      if (this.dom.topRestartBtn) {
        const showRestart = step > 1;
        this.dom.topRestartBtn.hidden = !showRestart;
        this.dom.topRestartBtn.classList.toggle('is-hidden', !showRestart);
      }

      const pct = Math.min(step * 20, 100);
      if (this.dom.progressFill) {
        this.dom.progressFill.style.width = `${pct}%`;
      }
      if (this.dom.progressBadge) {
        this.dom.progressBadge.style.left = `${pct}%`;
      }
      if (this.dom.progressStepNum) {
        this.dom.progressStepNum.textContent = step === 6 ? '✓' : step.toString();
      }
      if (this.dom.stepIndicator) {
        this.dom.stepIndicator.textContent = step === 6 ? '' : `Step ${step} of 5`;
      }

      this.dom.steps.forEach(stepEl => {
        const sNum = parseInt(stepEl.getAttribute('data-step'), 10);
        stepEl.classList.toggle('is-active', sNum === step);
      });

      if (this.dom.resultsContainer) {
        this.dom.resultsContainer.classList.toggle('is-active', step === 6);
      }

      // Highlight active selections in DOM
      if (step === 1) {
        document.querySelectorAll('#quiz-step-1 [data-option-val]').forEach(b => {
          b.classList.toggle('is-selected', b.getAttribute('data-option-val') === this.state.occasion);
        });
      } else if (step === 2) {
        document.querySelectorAll('#quiz-step-2 [data-option-val]').forEach(b => {
          const isSelected = Boolean(this.state.recipient && (b.getAttribute('data-option-val') === this.state.recipient));
          b.classList.toggle('is-selected', isSelected);
          if (b.hasAttribute('aria-checked')) {
            b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          }
        });
      } else if (step === 3) {
        this.updateStep3UIForGender();
        this.renderStyleCardsUI();
      } else if (step === 4) {
        this.updateStep4UIForGender();
        document.querySelectorAll('#quiz-step-4 [data-option-val]').forEach(b => {
          b.classList.toggle('is-selected', b.getAttribute('data-option-val') === this.state.personalization);
        });
      } else if (step === 5) {
        document.querySelectorAll('#quiz-step-5 [data-option-val]').forEach(b => {
          b.classList.toggle('is-selected', b.getAttribute('data-option-val') === this.state.budget);
        });
      } else if (step === 6) {
        this.computeAndRenderResults();
      }
    }

    /* ------------------------------------------------------------------------
       Real Shopify Catalogue Product Matching Engine
       ------------------------------------------------------------------------ */
    computeAndRenderResults() {
      this.renderSummaryBadges();

      if (this.catalogue.length === 0) {
        if (this.dom.productGrid) {
          this.dom.productGrid.innerHTML = '<div class="quiz-loading-spinner" aria-label="Loading pieces..."></div>';
        }
        if (this.dom.resultsCountText) {
          this.dom.resultsCountText.textContent = 'Curating your personalized recommendations...';
        }
        this.fetchCatalogue();
        return;
      }

      const results = this.filterCatalogue();
      this.matchingProducts = results;
      this.displayedCount = 0;

      if (this.dom.resultsCountText) {
        const count = results.length;
        if (count === 1) {
          this.dom.resultsCountText.textContent = 'We found 1 handcrafted piece tailored to your answers';
        } else if (count > 1) {
          this.dom.resultsCountText.textContent = `We found ${count} handcrafted pieces tailored to your answers`;
        } else {
          this.dom.resultsCountText.textContent = 'No exact matches found for this specific combination';
        }
      }

      if (results.length === 0) {
        if (this.dom.productGrid) this.dom.productGrid.innerHTML = '';
        if (this.dom.loadMoreWrap) this.dom.loadMoreWrap.style.display = 'none';
        if (this.dom.emptyState) this.dom.emptyState.style.display = 'block';
      } else {
        if (this.dom.emptyState) this.dom.emptyState.style.display = 'none';
        if (this.dom.productGrid) this.dom.productGrid.innerHTML = '';
        this.renderNextProductBatch();
      }
    }

    filterCatalogue() {
      const { occasion, recipient, categories, personalization, budget, gender } = this.state;
      const isMen = gender ? (gender === 'men') : (this.inferGenderFromRecipient(recipient) === 'men');
      const isAllPrices = !budget || budget === 'all';
      const isNotSureCategory = !categories.length || categories.includes('Not Sure');

      // 1. Determine active currency & budget bounds (Direct market prices, no arbitrary multipliers!)
      const tiers = this.getBudgetTiers(this.config.currency);
      let budgetMin = 0;
      let budgetMax = Infinity;

      if (!isAllPrices) {
        if (tiers[budget]) {
          budgetMin = tiers[budget].min;
          budgetMax = tiers[budget].max;
        } else if (budget === 'under-5000') {
          budgetMin = 0; budgetMax = 5000;
        } else if (budget === '5000-10000') {
          budgetMin = 5000; budgetMax = 10000;
        } else if (budget === '10000-30000') {
          budgetMin = 10000; budgetMax = 30000;
        } else if (budget === 'above-30000') {
          budgetMin = 30000; budgetMax = Infinity;
        }
      }

      const scoredProducts = [];

      for (let i = 0; i < this.catalogue.length; i++) {
        const product = this.catalogue[i];
        const pType = (product.product_type || '').toLowerCase();
        const pTitle = (product.title || '').toLowerCase();
        const pTags = (product.tags || []).map(t => t.trim().toLowerCase());

        // Availability check: must have at least one purchasable variant in this market
        const hasAvailableVariant = (product.variants || []).some(v => v.available === true || v.available === undefined);
        if (!hasAvailableVariant) continue;

        let relevanceScore = 0;

        // --- FILTER 1: Gender & Recipient Exclusions ---
        const hasMenTag = pTags.some(t => t === 'for him' || t === 'for men' || t === 'men');
        const hasWomenTag = pTags.some(t => t === 'for her' || t === 'for women' || t === 'women');
        const hasTeensTag = pTags.some(t => t === 'for teens' || t === 'for teen' || t === 'teens' || t.includes('teen'));
        const isCuff = pType.includes('cufflink') || /\bcufflinks?\b/i.test(pTitle);
        const isBrooch = pType.includes('brooch') || pTags.includes('brooch') || /\bbrooch(es)?\b/i.test(pTitle);

        if (isMen) {
          // For Men:
          // 1. Both men tag and teen tag must be present
          // 2. Or is a cufflink, brooch with men tag, or exclusively men's piece
          const matchesMen = (hasMenTag && hasTeensTag) || isCuff || (isBrooch && hasMenTag) || (hasMenTag && !hasWomenTag);
          if (!matchesMen) {
            continue;
          }
          relevanceScore += (hasMenTag && hasTeensTag) ? 160 : (isBrooch ? 140 : 120);
        } else {
          // For Women:
          // 1. If product has men tags, both women tag and teen tag must be present (brooches with women tag are valid)
          // 2. Normal women products must have women tag (or not be for men)
          let matchesWomen = false;
          if (isBrooch) {
            matchesWomen = hasWomenTag || (!hasMenTag && !isCuff);
          } else if (hasMenTag || isCuff) {
            matchesWomen = hasWomenTag && hasTeensTag;
          } else {
            matchesWomen = hasWomenTag || (!hasMenTag && !isCuff);
          }
          if (!matchesWomen) {
            continue;
          }
          relevanceScore += 100;
        }

        // --- FILTER 2: Jewellery Category (Step 3) ---
        let matchesCategory = false;
        if (isNotSureCategory) {
          matchesCategory = true;
          relevanceScore += 50;
        } else {
          for (const cat of categories) {
            // Necklaces / Chains
            if (cat === 'Necklaces' || cat === 'Chains & Necklaces') {
              const isNeck = pType.includes('necklace') || pTags.includes('necklace') || pTags.includes('necklaces') || /\b(necklaces?|chains?)\b/i.test(pTitle);
              if (isNeck) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Rings / Signet Bands
            else if (cat === 'Rings' || cat === 'Rings & Signet Bands') {
              const isRing = (pType === 'ring' || pType === 'rings' || pTags.includes('ring') || pTags.includes('rings') || /\b(rings?|signet|bands?)\b/i.test(pTitle)) && !pType.includes('earring') && !pTitle.includes('earring');
              if (isRing) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Bracelets / Cords
            else if (cat === 'Bracelets' || cat === 'Bracelets & Cords') {
              const isBracelet = pType.includes('bracelet') || pType.includes('bangle') || pTags.includes('bracelet') || pTags.includes('bracelets') || pTags.includes('bangle') || /\b(bracelets?|bangles?|cords?|thread)\b/i.test(pTitle);
              if (isBracelet) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Earrings (Women)
            else if (cat === 'Earrings') {
              const isEarring = pType.includes('earring') || pTags.includes('earring') || pTags.includes('earrings') || /\bearrings?\b/i.test(pTitle);
              if (isEarring) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Charms & Pendants / Pendants & Dog Tags
            else if (cat === 'Charms & Pendants' || cat === 'Charms and Pendants' || cat === 'Pendants & Dog Tags') {
              const isPendant = pType.includes('pendant') || pType.includes('charm') || pTags.includes('pendant') || pTags.includes('charm') || /\b(pendants?|charms?|dog\s*tags?|tags?)\b/i.test(pTitle);
              if (isPendant) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Brooches
            else if (cat === 'Brooches') {
              if (isBrooch) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
            // Cufflinks (Men)
            else if (cat === 'Cufflinks') {
              const isCufflink = pType.includes('cufflink') || pTags.includes('cufflink') || /\bcufflinks?\b/i.test(pTitle);
              if (isCufflink) {
                matchesCategory = true;
                relevanceScore += 100;
                break;
              }
            }
          }
        }

        if (!matchesCategory) continue;

        // --- FILTER 3: Budget Evaluation (Variant-Aware Matching in Market Currency) ---
        const variants = (product.variants && product.variants.length > 0) ? product.variants : [];
        if (variants.length === 0) continue;

        let displayVariant = null;
        let displayPrice = 0;
        let displayComparePrice = null;
        let matchingVariants = [];

        if (isAllPrices) {
          matchingVariants = variants.filter(v => v.available !== false);
          displayVariant = matchingVariants[0] || variants[0];
          displayPrice = parseFloat(displayVariant.price) || 0;
          displayComparePrice = displayVariant.compare_at_price ? parseFloat(displayVariant.compare_at_price) : null;
          relevanceScore += 80;
        } else {
          // Variant-aware budget matching: checks all purchasable variants against selected budget range
          matchingVariants = variants.filter(v => {
            if (v.available === false) return false;
            const price = parseFloat(v.price);
            return !isNaN(price) && price >= budgetMin && price <= budgetMax;
          });

          if (matchingVariants.length === 0) {
            continue;
          }

          // Pick the best qualifying variant in budget range (lowest qualifying variant price)
          matchingVariants.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
          displayVariant = matchingVariants[0];
          displayPrice = parseFloat(displayVariant.price);
          displayComparePrice = displayVariant.compare_at_price ? parseFloat(displayVariant.compare_at_price) : null;
          relevanceScore += 80;
        }

        // --- FILTER 4: Occasion Matching (Relevance Boost) ---
        let matchesOccasion = false;
        if (!occasion || occasion === 'Not Sure / Explore All') {
          matchesOccasion = true;
          relevanceScore += 40;
        } else if (occasion === 'Birthday') {
          matchesOccasion = pTags.includes('birthday');
        } else if (occasion === 'Anniversary') {
          matchesOccasion = pTags.includes('anniversary');
        } else if (occasion === 'Wedding & Engagement' || occasion === 'Wedding or Engagement') {
          matchesOccasion = pTags.includes('wedding & engagement') || pTags.includes('wedding') || pTags.includes('wedding guest');
        } else if (occasion === 'Graduation & Milestones' || occasion === 'Graduation or Milestone') {
          matchesOccasion = pTags.includes('graduation') || pTags.includes('milestones');
        } else if (occasion === 'Everyday Classics') {
          matchesOccasion = pTags.includes('everyday classics') || pTags.includes('classic');
        } else if (occasion === 'Cocktail & Evening') {
          matchesOccasion = pTags.includes('cocktail & going out') || pTags.includes('cocktail and going out');
        } else if (occasion === 'Festive & Cultural' || occasion === 'Cultural & Festive Celebration') {
          matchesOccasion = pTags.includes('fancy and opulent');
        } else if (occasion === 'Just Because' || occasion.includes('Just Because')) {
          matchesOccasion = pTags.includes('everyday classics') || pTags.includes('classic');
        }

        if (matchesOccasion) {
          relevanceScore += 120;
        }

        // --- FILTER 5: Style Matching (Relevance Boost) ---
        let matchesStyle = false;
        if (!personalization || personalization === 'Surprise Me' || personalization === 'Not Sure Yet') {
          matchesStyle = true;
          relevanceScore += 50;
        } else if (personalization === 'Name Jewellery') {
          matchesStyle = pTags.includes('name jewelry') || pTags.includes('name initials') || /\bname\b/i.test(pTitle);
        } else if (personalization === 'Lucky Numbers & Initials') {
          matchesStyle = pTags.includes('lucky number') || pTags.includes('name initials') || /\b(number|initial|year)\b/i.test(pTitle);
        } else if (personalization === 'Love Letters') {
          matchesStyle = pTags.includes('love letter') || /\b(letter|secret letter)\b/i.test(pTitle);
        } else if (personalization === 'Solitaire & Classic') {
          matchesStyle = pTags.includes('solitaire') || pTags.includes('soliataire') || pTags.includes('classic');
        } else if (personalization === 'Bright & Colourful') {
          matchesStyle = pTags.includes('bright and colorful');
        } else if (personalization === 'Bold & Opulent') {
          matchesStyle = pTags.includes('fancy and opulent');
        } else if (personalization === 'Timeless & Minimal') {
          matchesStyle = pTags.includes('everyday classics') || pTags.includes('classic') || pTags.includes('office wear');
        } else {
          // Legacy mappings
          if (personalization === 'Initials or Names' || personalization === 'Dog Tags, Initials & Numbers') {
            matchesStyle = pTags.some(t => t.includes('name') || t.includes('initial') || t.includes('number') || t.includes('letter')) || /\b(initial|name|number|letter|dog\s*tag)\b/i.test(pTitle);
          } else if (personalization === 'Birthstones or Gemstones') {
            matchesStyle = pTags.includes('bright and colorful') || pTags.some(t => t.includes('stone') || t.includes('color') || t.includes('emerald') || t.includes('ruby') || t.includes('sapphire'));
          } else if (personalization === 'Engraving & Special Numbers' || personalization === 'Engraved Coordinates & Cord Bracelets') {
            matchesStyle = pTags.includes('lucky number') || pTags.includes('love letter') || pTags.some(t => t.includes('engrav') || t.includes('personaliz') || t.includes('cord') || t.includes('lucky number')) || /\b(engrav|cord|secret|number)\b/i.test(pTitle);
          } else if (personalization === 'Signet Rings & Statement Bands') {
            matchesStyle = pTags.some(t => t.includes('signet') || t.includes('band')) || /\b(signet|band|statement)\b/i.test(pTitle);
          } else if (personalization.includes('No Personalisation') || personalization.includes('Classic') || personalization.includes('Minimalist')) {
            matchesStyle = pTags.some(t => t.includes('classic') || t.includes('solitaire') || t.includes('everyday'));
          }
        }

        if (matchesStyle) {
          relevanceScore += 150;
        }

        scoredProducts.push({
          product,
          relevanceScore,
          displayVariant,
          displayPrice,
          displayComparePrice,
          matchingVariants: isAllPrices ? variants : matchingVariants,
          matchesOccasion,
          matchesStyle
        });
      }

      // Sort by relevance score descending, then by price ascending (matching live site sort)
      scoredProducts.sort((a, b) => {
        if (b.relevanceScore !== a.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }
        return a.displayPrice - b.displayPrice;
      });

      return scoredProducts;
    }

    renderSummaryBadges() {
      if (!this.dom.summaryBadges) return;
      const { occasion, recipient, categories, personalization, budget } = this.state;
      const cur = (this.config.currency || 'USD').toUpperCase();
      const tiers = this.getBudgetTiers(cur);

      let budgetLabel = 'All Budgets';
      if (budget && tiers[budget]) {
        budgetLabel = tiers[budget].label;
      }

      const pills = [
        { label: 'Occasion', val: occasion, step: 1 },
        { label: 'Recipient', val: recipient, step: 2 },
        { label: 'Jewellery', val: categories.join(', ') || 'Any Jewellery', step: 3 },
        { label: 'Style', val: personalization || 'Any Style', step: 4 },
        { label: 'Budget', val: budgetLabel, step: 5 }
      ];

      this.dom.summaryBadges.innerHTML = pills.map(p => `
        <div class="quiz-summary-pill" role="button" tabindex="0" data-jump-step="${p.step}" title="Click to edit ${p.label}">
          <span class="quiz-summary-pill__label">${this.escapeHtml(p.label)}:</span>
          <span class="quiz-summary-pill__val">${this.escapeHtml(p.val)}</span>
        </div>
      `).join('');

      this.dom.summaryBadges.querySelectorAll('[data-jump-step]').forEach(el => {
        el.addEventListener('click', () => {
          const step = parseInt(el.getAttribute('data-jump-step'), 10);
          this.goToStep(step);
        });
        el.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            const step = parseInt(el.getAttribute('data-jump-step'), 10);
            this.goToStep(step);
          }
        });
      });
    }

    renderNextProductBatch() {
      if (!this.dom.productGrid) return;

      const start = this.displayedCount;
      const end = Math.min(start + PRODUCTS_PER_PAGE, this.matchingProducts.length);
      const batch = this.matchingProducts.slice(start, end);

      const html = batch.map(item => this.buildProductCardHtml(item)).join('');
      this.dom.productGrid.insertAdjacentHTML('beforeend', html);
      this.displayedCount = end;

      // Initialize wishlist buttons for freshly rendered pieces
      if (window.AmolihWishlist && typeof window.AmolihWishlist.init === 'function') {
        try { window.AmolihWishlist.init(); } catch (e) { }
      }

      // Update Load More button
      if (this.dom.loadMoreWrap) {
        if (this.displayedCount < this.matchingProducts.length) {
          const remaining = this.matchingProducts.length - this.displayedCount;
          this.dom.loadMoreWrap.style.display = 'block';
          if (this.dom.loadMoreBtn) {
            this.dom.loadMoreBtn.textContent = `Load More Gifts (${remaining} remaining)`;
          }
        } else {
          this.dom.loadMoreWrap.style.display = 'none';
        }
      }
    }

    buildProductCardHtml(item) {
      const p = item.product;
      const v = item.displayVariant;
      const title = this.escapeHtml(p.title);
      const handle = (p.handle || '').trim();

      // Canonical root URL: strictly respect actual browser pathname prefix
      const path = window.location.pathname || '';
      let cleanRoot = '';
      if (path.startsWith('/en-gb')) {
        cleanRoot = '/en-gb';
      } else if (path.startsWith('/en-in')) {
        cleanRoot = '/en-in';
      } else if (this.config && this.config.rootUrl && this.config.rootUrl !== '/') {
        cleanRoot = this.config.rootUrl.replace(/\/$/, '');
      } else if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/') {
        cleanRoot = window.Shopify.routes.root.replace(/\/$/, '');
      }

      const primaryImg = p.images && p.images[0] ? p.images[0].src : '';
      const basePrice = item.displayPrice !== undefined ? item.displayPrice : (v ? parseFloat(v.price) : 0);
      const baseComparePrice = item.displayComparePrice !== undefined && item.displayComparePrice !== null ? item.displayComparePrice : (v && v.compare_at_price ? parseFloat(v.compare_at_price) : 0);

      // Collect images and map to variants (matching AMOLIH theme collection cards)
      const rawImages = (p.images && p.images.length > 0) ? p.images : (primaryImg ? [{ id: '', src: primaryImg }] : []);

      // If we have a selected variant (v) with a featured image, ensure that image is first
      let sortedImages = [...rawImages];
      if (v && v.featured_image && v.featured_image.src) {
        const vImgClean = v.featured_image.src.split('?')[0].replace(/^https?:/, '').replace(/^\/\//, '');
        const vIdx = sortedImages.findIndex(img => {
          if (img.id && v.featured_image.id && String(v.featured_image.id) === String(img.id)) return true;
          const imgClean = (img.src || '').split('?')[0].replace(/^https?:/, '').replace(/^\/\//, '');
          return imgClean === vImgClean;
        });
        if (vIdx > 0) {
          const [vImg] = sortedImages.splice(vIdx, 1);
          sortedImages.unshift(vImg);
        }
      }

      // Qualifying variants within user's selected budget
      const qualifyingVariants = (item.matchingVariants && item.matchingVariants.length > 0)
        ? item.matchingVariants
        : (v ? [v] : (p.variants || []));

      const slides = sortedImages.map((img, idx) => {
        let matchedVariant = null;
        if (idx === 0 && v) {
          // Slide 0 strictly uses the qualifying variant in budget
          matchedVariant = v;
        } else if (qualifyingVariants.length > 0) {
          const imgClean = (img.src || '').split('?')[0].replace(/^https?:/, '').replace(/^\/\//, '');
          matchedVariant = qualifyingVariants.find(vr => {
            if (vr.featured_image && vr.featured_image.src) {
              const vrImgClean = vr.featured_image.src.split('?')[0].replace(/^https?:/, '').replace(/^\/\//, '');
              if (img.id && vr.featured_image.id && String(vr.featured_image.id) === String(img.id)) return true;
              if (vrImgClean === imgClean) return true;
            }
            return false;
          });

          if (!matchedVariant && img.variant_ids && img.variant_ids.length > 0) {
            matchedVariant = qualifyingVariants.find(vr => img.variant_ids.includes(vr.id));
          }

          if (!matchedVariant) {
            matchedVariant = v;
          }
        }

        const effectiveVariant = matchedVariant || v;
        const vPrice = effectiveVariant ? parseFloat(effectiveVariant.price) : basePrice;
        const vComparePrice = effectiveVariant && effectiveVariant.compare_at_price ? parseFloat(effectiveVariant.compare_at_price) : 0;
        const vIsOnSale = vComparePrice > vPrice;

        let slideUrl = `${cleanRoot}/products/${encodeURIComponent(handle)}`;
        if (effectiveVariant && effectiveVariant.id) {
          slideUrl += `?variant=${encodeURIComponent(effectiveVariant.id)}`;
        }

        return {
          id: img.id || '',
          src: img.src,
          alt: img.alt || title,
          url: slideUrl,
          variantId: effectiveVariant ? effectiveVariant.id : '',
          variantTitle: effectiveVariant ? effectiveVariant.title : '',
          price: vPrice,
          comparePrice: vComparePrice,
          isOnSale: vIsOnSale,
          formattedPrice: this.formatCurrency(vPrice),
          formattedComparePrice: vIsOnSale ? this.formatCurrency(vComparePrice) : ''
        };
      });

      const initialSlide = slides[0] || {
        src: (v && v.featured_image && v.featured_image.src) ? v.featured_image.src : primaryImg,
        alt: title,
        url: v && v.id ? `${cleanRoot}/products/${encodeURIComponent(handle)}?variant=${v.id}` : `${cleanRoot}/products/${encodeURIComponent(handle)}`,
        variantId: v ? v.id : '',
        variantTitle: v ? v.title : '',
        price: basePrice,
        comparePrice: baseComparePrice,
        isOnSale: baseComparePrice > basePrice,
        formattedPrice: this.formatCurrency(basePrice),
        formattedComparePrice: baseComparePrice > basePrice ? this.formatCurrency(baseComparePrice) : ''
      };

      const variantOptionsJson = v && v.options ? this.escapeHtml(JSON.stringify(v.options)) : '[]';

      return `
        <div class="quiz-product-card" id="quiz-product-${p.id}" data-product-id="${p.id}" data-product-url="${initialSlide.url}" data-current-slide="0">
          <div class="quiz-product-card__content">
            <div class="quiz-product-card__media-wrap">
              <a href="${initialSlide.url}" class="quiz-product-card__media-link" aria-label="${title}">
                <span class="quiz-product-card__badge-sale" ${initialSlide.isOnSale ? '' : 'style="display:none;"'}>Sale</span>
                <div class="quiz-product-card__slides">
                  ${slides.map((s, i) => `
                    <div
                      class="quiz-product-card__slide ${i === 0 ? 'is-active' : ''}"
                      data-slide-index="${i}"
                      data-product-url="${s.url}"
                      data-variant-id="${s.variantId}"
                      data-variant-title="${this.escapeHtml(s.variantTitle)}"
                      data-price="${s.price}"
                      data-compare-price="${s.comparePrice}"
                      data-formatted-price="${s.formattedPrice}"
                      data-formatted-compare-price="${s.formattedComparePrice}"
                      data-is-on-sale="${s.isOnSale}"
                    >
                      <img
                        src="${this.getSizedImageUrl(s.src, 800)}"
                        alt="${this.escapeHtml(s.alt)}"
                        class="quiz-product-card__img"
                        loading="lazy"
                        width="400"
                        height="400"
                      />
                    </div>
                  `).join('')}
                </div>
              </a>
              ${slides.length > 1 ? `
                <slideshow-arrows position="center" class="quiz-card-arrows">
                  <button
                    type="button"
                    class="slideshow-control slideshow-control--previous button button-unstyled button-unstyled--transparent flip-x"
                    aria-label="Previous slide"
                    data-quiz-arrow="prev"
                  >
                    <span class="svg-wrapper icon-arrow">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    class="slideshow-control slideshow-control--next button button-unstyled button-unstyled--transparent"
                    aria-label="Next slide"
                    data-quiz-arrow="next"
                  >
                    <span class="svg-wrapper icon-arrow">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>
                    </span>
                  </button>
                </slideshow-arrows>
              ` : ''}
              <button
                type="button"
                class="amolih-wishlist-btn amolih-wishlist-btn--card"
                data-wishlist-button
                data-product-id="${p.id}"
                data-product-handle="${handle}"
                data-product-title="${title}"
                data-variant-id="${initialSlide.variantId}"
                data-variant-title="${this.escapeHtml(initialSlide.variantTitle)}"
                data-variant-image="${this.escapeHtml(initialSlide.src)}"
                data-variant-price="${initialSlide.price}"
                data-variant-compare-at-price="${initialSlide.comparePrice || 0}"
                data-variant-options='${variantOptionsJson}'
                data-display-title="${title}"
                aria-label="Add to wishlist"
                aria-pressed="false"
              >
                <svg
                  class="amolih-wishlist-btn__icon amolih-wishlist-btn__icon--outline"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/>
                </svg>
                <svg
                  class="amolih-wishlist-btn__icon amolih-wishlist-btn__icon--filled"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"/>
                </svg>
              </button>
            </div>
            <div class="quiz-product-card__info">
              <a href="${initialSlide.url}" class="quiz-product-card__title-link">
                <h3 class="quiz-product-card__title">${title}</h3>
              </a>
              <div class="quiz-product-card__price-row">
                <span class="quiz-product-card__price">${initialSlide.formattedPrice}</span>
                <span class="quiz-product-card__compare-price" ${initialSlide.isOnSale ? '' : 'style="display:none;"'}>${initialSlide.formattedComparePrice}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    formatCurrency(amount) {
      const cur = (this.config.currency || 'USD').toUpperCase();
      let num = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
      if (cur === 'INR') {
        num = Math.round(num);
      }
      try {
        if (cur === 'INR') {
          return '₹' + num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        const localeMap = { USD: 'en-US', GBP: 'en-GB', CAD: 'en-CA', AUD: 'en-AU', EUR: 'de-DE' };
        return new Intl.NumberFormat(localeMap[cur] || 'en-US', {
          style: 'currency',
          currency: cur,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(num);
      } catch (e) {
        const sym = this.config.currencySymbol || (cur === 'INR' ? '₹' : '$');
        const sep = (sym === '$' || sym === '£' || sym === '€' || sym === '₹' || sym.endsWith(' ')) ? '' : ' ';
        return sym + sep + num.toFixed(2);
      }
    }

    getSizedImageUrl(src, width) {
      if (!src) return '';
      if (src.includes('?')) {
        return `${src}&width=${width}`;
      }
      return `${src}?width=${width}`;
    }

    escapeHtml(str) {
      if (!str) return '';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  // Self-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new AmolihGiftQuiz());
  } else {
    new AmolihGiftQuiz();
  }
})();